import { formatUsd, sumNoProofUsd, useClaimsStore } from "@/features/claims";
import iconFind from "../assets/icon-advantage-1.svg";
import iconFiled from "../assets/icon-advantage-2.svg";
import iconNotify from "../assets/icon-advantage-3.svg";
import iconTrack from "../assets/icon-advantage-4.svg";
import watermark from "../assets/bg-claims-watermark.svg";
import guaranteeIcon from "../assets/icon-guarantee.svg";
import logoAmazon from "../assets/logo-amazon.svg";
import logoTmobile from "../assets/logo-tmobile.svg";
import logoTransunion from "../assets/logo-transunion.svg";

const ADVANTAGES = [
  {
    icon: iconFind,
    lead: "Find money",
    rest: " you're owed across hundreds of active settlements",
  },
  {
    icon: iconFiled,
    lead: "Claims filed",
    rest: " for you automatically — no paperwork, no stress",
  },
  {
    icon: iconNotify,
    leadBefore: "Get notified the moment a ",
    lead: "new settlement",
    rest: " matches you",
  },
  {
    icon: iconTrack,
    lead: "Track every payout",
    rest: ", from filed to deposited",
  },
] as const;

const FALLBACK_CLAIMS = [
  { name: "Amazon", logo: logoAmazon },
  { name: "T-Mobile", logo: logoTmobile },
  { name: "TransUnion", logo: logoTransunion },
] as const;
const FALLBACK_TOTAL = 1717;
const MAX_VISIBLE_CLAIMS = 3;

export function CheckoutAdvantages() {
  const selected = useClaimsStore((s) => s.selected);
  const claims =
    selected.length > 0
      ? selected.map((claim) => ({
          key: claim.id,
          name: claim.company,
          logo: claim.iconUrl,
        }))
      : FALLBACK_CLAIMS.map((claim) => ({
          key: claim.name,
          name: claim.name,
          logo: claim.logo,
        }));
  const visibleClaims = claims.slice(0, MAX_VISIBLE_CLAIMS);
  const overflowCount = Math.max(0, claims.length - MAX_VISIBLE_CLAIMS);
  const claimCount = claims.length;
  const total = selected.length > 0 ? sumNoProofUsd(selected) : FALLBACK_TOTAL;

  return (
    <section className="flex flex-col gap-4 px-4">
      <h2 className="text-center font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
        From Missed Settlements
        <br />
        to <span className="text-mf-green">Money in Your Pocket</span>
      </h2>

      <div className="flex flex-col gap-4 rounded-[24px] bg-mf-neutral-50 p-4">
        <div className="flex flex-col gap-2">
          {ADVANTAGES.map((item) => (
            <div key={item.lead} className="flex items-center gap-2">
              <div className="flex size-[52px] shrink-0 items-center justify-center rounded-[26px] bg-white p-3 shadow-[0_0_8px_rgba(26,22,19,0.03)]">
                <span className="relative size-7 overflow-hidden">
                  <img src={item.icon} alt="" className="size-full" />
                </span>
              </div>
              <p className="min-w-0 flex-1 text-sm leading-4">
                {"leadBefore" in item && (
                  <span className="text-mf-neutral-600">{item.leadBefore}</span>
                )}
                <span className="font-bold text-mf-neutral-900">
                  {item.lead}
                </span>
                <span className="text-mf-neutral-600">{item.rest}</span>
              </p>
            </div>
          ))}
        </div>

        <div className="relative flex flex-col gap-3 overflow-hidden rounded-xl bg-[linear-gradient(246.51deg,rgba(17,170,89,0.2)_0%,rgba(107,206,62,0.2)_100%)] p-4">
          <img
            src={watermark}
            alt=""
            className="pointer-events-none absolute inset-0 size-full object-cover"
          />

          <div className="relative flex flex-col gap-[7px]">
            <p className="text-sm font-bold leading-4 text-mf-neutral-900">
              {claimCount} {claimCount === 1 ? "claim" : "claims"} waiting to be
              filed
            </p>
            <div className="flex items-center gap-1">
              {visibleClaims.map((claim) => (
                <ClaimChip
                  key={claim.key}
                  name={claim.name}
                  logo={claim.logo}
                />
              ))}
              {overflowCount > 0 ? (
                <span className="inline-flex items-center justify-center overflow-hidden rounded bg-white p-1">
                  <span className="text-xs font-bold leading-4 text-mf-neutral-600">
                    +{overflowCount}
                  </span>
                </span>
              ) : null}
            </div>
          </div>

          <div className="relative flex flex-col justify-center">
            <p className="text-sm leading-4 text-mf-neutral-900/60">
              Total potential claim value
            </p>
            <div className="flex items-center gap-1">
              <span className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
                {formatUsd(total)}
              </span>
              <span className="flex items-center justify-center gap-1 overflow-hidden rounded-full bg-[linear-gradient(75.68deg,#4fa82b_0%,#11aa59_100%)] py-1 pl-1 pr-2">
                <span className="relative size-4 overflow-hidden">
                  <img src={guaranteeIcon} alt="" className="size-full" />
                </span>
                <span className="text-xs font-bold leading-4 text-white">
                  Guarantee
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ClaimChip({ name, logo }: { name: string; logo: string | null }) {
  return (
    <span className="inline-flex items-center justify-center gap-1 overflow-hidden rounded bg-white p-1">
      {logo ? (
        <span className="relative size-4 overflow-hidden">
          <img src={logo} alt="" className="size-full object-contain" />
        </span>
      ) : null}
      <span className="text-xs font-bold leading-4 text-mf-neutral-900">
        {name}
      </span>
    </span>
  );
}
